<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { useRoute } from 'vue-router';
import { DateTime } from 'luxon';
import { useToast } from 'primevue/usetoast';
import useVuelidate from '@vuelidate/core';
import routerKey from '@/router/routerKey';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { formatDateAllowLanguage } from '@/utils/dateUtils';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { required, helpers } from '@vuelidate/validators';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';

const props = defineProps<{
  email?: string;
}>();

const route = useRoute();
const toast = useToast();
const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const myQuoteStore = useMyQuoteStore();
const completedList: any = ref([]);
const isAddReminder = ref(false);
const inputAddReminder = ref();
const isShowError = ref(false);
const isExpandNewReminder = ref(true);
const isExpandCompleteReminder = ref(false);
const minDate = ref(new Date());
const newReminders = computed(() => myQuoteStore.findNewReminders());
const completedReminders = computed(() => myQuoteStore.findCompletedReminders());

const newReminder = ref({
  textNote: '',
  reminderDate: null,
  email: '',
  sts: 'NEW'
});

const rules = ref({
  textNote: {
    required: helpers.withMessage(t('validate.required'), required)
  },
  reminderDate: {
    required: helpers.withMessage(t('validate.required'), required)
  }
});

const v$ = useVuelidate(rules, newReminder);

const formatDate = (date) => {
  return date ? DateTime.fromJSDate(new Date(date)).toFormat('yyyy-LL-dd') : '';
};

const addReminder = async () => {
  isShowError.value = true;
  if (!newReminder.value.textNote || !newReminder.value.reminderDate) {
    return;
  }
  const param = { ...newReminder.value, email: props.email };
  param.reminderDate = formatDate(param.reminderDate);

  await myQuoteStore.addReminder(param).then(() => {
    toast.add({ severity: 'success', summary: '', detail: t('task_list.add_a_reminder_success'), life: 3000 });
    myQuoteStore.getReminders({ email: props.email });
    newReminder.value.textNote = '';
    newReminder.value.reminderDate = null;
  });
  isShowError.value = false;
};

const openInputAddReminder = () => {
  isAddReminder.value = true;
};

const isHasRole = () => {
  if (route.name === routerKey.MY_QUOTE && authorizations.my_quote.reminder_add) {
    return true;
  } else if (route.name === routerKey.QUOTE_MANAGEMENT && authorizations.quote_management.reminder_add) {
    return true;
  } else {
    return false;
  }
};

const handleDeleteReminder = (reminder) => {
  myQuoteStore.deleteReminder(reminder.sk).then(() => {
    toast.add({ severity: 'success', summary: '', detail: t('task_list.delete_reminder_success'), life: 3000 });
    myQuoteStore.getReminders({ email: props.email });
  });
};

const handleClickReminder = (reminder) => {
  if (reminder.sts === 'NEW') {
    reminder.sts = 'COMPLETED';
  } else {
    reminder.sts = 'NEW';
  }
  myQuoteStore.updateReminder(reminder);
};

const isVAlidRouterName = (routerName) => {
  return route?.name === routerName;
};
const canAddNewTask = (isVAlidRouterName(routerKey.MY_QUOTE) && authorizations.my_quote.reminder_add) || (isVAlidRouterName(routerKey.QUOTE_MANAGEMENT) && authorizations.quote_management.reminder_add);
const canDeleteTask = (isVAlidRouterName(routerKey.MY_QUOTE) && authorizations.my_quote.reminder_delete) || (isVAlidRouterName(routerKey.QUOTE_MANAGEMENT) && authorizations.quote_management.reminder_delete);

const closeInputAdd = () => {
  isAddReminder.value = false;
  isShowError.value = false;
};

const handleExpandNewReminder = (isExpand) => {
  isExpandNewReminder.value = !isExpand;
};

const handleExpandCompleteReminder = (isExpand) => {
  isExpandCompleteReminder.value = !isExpand;
};

const checkIsExpirationDate = (reminderDate) => {
  const cDate = DateTime.fromJSDate(new Date());
  const rDate = DateTime.fromJSDate(new Date(reminderDate));
  return cDate > rDate;
};

watch(completedReminders, () => {
  completedList.value = completedReminders.value.map((item) => item.sk);
});
</script>
<template>
  <div class="surface-section task-list">
    <div v-if="!isAddReminder && canAddNewTask" class="text-color-secondary mb-2 text-center cursor-pointer" @click="openInputAddReminder()">+ {{ t('task_list.add_a_reminder') }}</div>
    <div v-if="isAddReminder && isHasRole()" class="add-reminder mt-2 mb-3 gap-3">
      <div class="form-input">
        <div class="input-content">
          <InputText ref="inputAddReminder" v-model="newReminder.textNote" :placeholder="$t('note.content_placeholder')" maxlength="1000" class="w-full" />
          <span class="text-red-500" v-if="isShowError && v$.textNote.$invalid">
            {{ v$.textNote.required.$message }}
          </span>
        </div>

        <div class="input-date">
          <DatePicker dateFormat="dd-M-yy" :min-date="minDate" v-model="newReminder.reminderDate" showIcon>
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
          <p class="text-red-500" v-if="isShowError && v$.reminderDate.$invalid">
            {{ v$.textNote.required.$message }}
          </p>
        </div>
      </div>
      <div class="task-btn">
        <Button class="p-button-text text-primary" icon="pi pi-check" :label="t('common.buttons.save')" @click="addReminder()" />
        <Button class="p-button-text text-primary" icon="pi pi-times" :label="t('common.buttons.cancel')" @click="closeInputAdd()" />
      </div>
    </div>
    <div class="list-none p-0 m-0">
      <DataTable :value="isExpandNewReminder ? newReminders : []" :sortField="'reminderDate'" :sortOrder="1" dataKey="sk">
        <TableColumn field="sk" class="m-w-160">
          <template #header>
            <Button v-if="canDeleteTask && isExpandNewReminder" @click="handleExpandNewReminder(isExpandNewReminder)" :label="$t('common.to_action')" icon="pi pi-chevron-down" class="p-button-text button-action"></Button>
            <Button v-if="canDeleteTask && !isExpandNewReminder" @click="handleExpandNewReminder(isExpandNewReminder)" :label="$t('common.to_action')" icon="pi pi-chevron-right" class="p-button-text button-action"></Button>
          </template>
          <template #body="{ data }">
            <Checkbox @click="handleClickReminder(data)" class="mr-2" :value="data.sk" />
          </template>
        </TableColumn>
        <TableColumn field="textNote" header="" class="w-full">
          <template #body="{ data }">
            {{ data.textNote }}
          </template>
        </TableColumn>
        <TableColumn :header="$t('task_list.due_date')" sortable sortField="reminderDate" filterField="reminderDate" class="m-w-160">
          <template #body="{ data }">
            <div class="flex align-items-center">
              <span :class="{ 'px-3 py-2 bg-red text-white w-max select-none': checkIsExpirationDate(data.reminderDate), 'px-3 py-2 bg-normal w-max select-none': !checkIsExpirationDate(data.reminderDate) }">{{
                formatDateAllowLanguage(data.reminderDate, currentLanguage)
              }}</span>
            </div>
          </template>
        </TableColumn>
        <TableColumn class="m-w-160">
          <template #body="{ data }">
            <div class="flex justify-content-end" v-if="authorizations?.quote_management?.reminder_delete">
              <Button v-if="canDeleteTask" @click="handleDeleteReminder(data)" :label="$t('common.buttons.delete')" icon="pi pi-trash" class="p-button-text text-primary"></Button>
            </div>
          </template>
        </TableColumn>
      </DataTable>
    </div>
    <div class="list-none p-0 m-0 mt-2">
      <DataTable :value="isExpandCompleteReminder ? completedReminders : []" :sortField="'reminderDate'" :sortOrder="1" dataKey="sk">
        <TableColumn field="sk" class="m-w-160">
          <template #header>
            <Button v-if="isExpandCompleteReminder" @click="handleExpandCompleteReminder(isExpandCompleteReminder)" :label="$t('task_list.completed')" icon="pi pi-chevron-down" class="p-button-text button-action"></Button>
            <Button v-else @click="handleExpandCompleteReminder(isExpandCompleteReminder)" :label="$t('task_list.completed')" icon="pi pi-chevron-right" class="p-button-text button-action"></Button>
          </template>
          <template #body="{ data }">
            <Checkbox v-model="completedList" @click="handleClickReminder(data)" class="mr-2" :value="data.sk" />
          </template>
        </TableColumn>
        <TableColumn field="textNote" header="" class="w-full">
          <template #body="{ data }">
            {{ data.textNote }}
          </template>
        </TableColumn>
        <TableColumn :header="$t('task_list.due_date')" sortable sortField="reminderDate" filterField="reminderDate" class="m-w-160">
          <template #body="{ data }">
            <div class="flex align-items-center">
              <span :class="{ 'px-3 py-2 bg-red text-white w-max select-none': checkIsExpirationDate(data.reminderDate), 'px-3 py-2 bg-normal w-max select-none': !checkIsExpirationDate(data.reminderDate) }">{{
                formatDateAllowLanguage(data.reminderDate, currentLanguage)
              }}</span>
            </div>
          </template>
        </TableColumn>
        <TableColumn class="m-w-160">
          <template #body="{ data }">
            <div class="flex justify-content-end" v-if="authorizations?.quote_management?.reminder_delete">
              <Button v-if="canDeleteTask" @click="handleDeleteReminder(data)" :label="$t('common.buttons.delete')" icon="pi pi-trash" class="p-button-text text-primary"></Button>
            </div>
          </template>
        </TableColumn>
      </DataTable>
    </div>
  </div>
</template>
